"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { ArithmeticSheet } from "@/components/tools/worksheets-math/arithmetic-sheet";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import {
  CommonControls,
  defaultSettings,
  UnmetNotice,
  useCommonSettings,
  useWorksheetSeed,
} from "@/components/tools/worksheets-math/worksheet-kit";
import {
  type BorrowMode,
  generateSubtraction,
  type MissingMode,
  type SubtractionOptions,
} from "@/lib/tool-kit/worksheets/arithmetic";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const SUBTRACTION_WORKSHEET_GENERATOR_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Subtraction Practice",
  presetsLabel: "Quick levels",
  presets: {
    facts9: "Single-digit facts",
    facts20: "Facts within 20",
    twoNoBorrow: "2-digit, no borrowing",
    twoBorrow: "2-digit, borrowing",
    zeros: "Borrowing across zeros",
    negatives: "Negative answers",
  },
  minuendLabel: "Digits in the top number",
  subtrahendLabel: "Digits in the bottom number",
  subtrahendHint:
    "Without negative answers, the bottom number never has more digits than the top.",
  borrowLabel: "Borrowing (regrouping)",
  borrows: {
    any: "Allowed, not required",
    none: "None — no column borrows",
    always: "Required — at least one borrow",
    zeros: "Across a zero — such as 5003 − 1768",
  },
  zerosNotice:
    "Borrowing across zeros needs a top number with at least 3 digits. With fewer, every problem borrows in the ordinary way.",
  maxTopLabel: "Largest top number",
  maxTopNone: "No limit",
  allowNegative: "Allow negative answers",
  allowZero: "Include 0 as a number",
  unknownLabel: "What the student finds",
  unknowns: {
    answer: "The difference (9 − 4 = ?)",
    missing: "A missing number (9 − ? = 5 or ? − 4 = 5)",
    mixed: "A mix of both",
  },
  unknownHint: "Missing-number problems use the one-line layout.",
  regroupBoxes: "Regrouping boxes above the top number",
} as const;

type Fields = Omit<SubtractionOptions, "count" | "missing">;

const START: Fields = {
  minuendDigits: 2,
  subtrahendDigits: 2,
  borrow: "any",
  allowNegative: false,
  maxMinuend: 0,
  allowZero: false,
};

const PRESETS: Record<
  keyof typeof SUBTRACTION_WORKSHEET_GENERATOR_STRINGS.presets,
  Partial<Fields>
> = {
  facts9: { minuendDigits: 1, subtrahendDigits: 1, allowZero: true },
  facts20: { minuendDigits: 2, subtrahendDigits: 1, maxMinuend: 20 },
  twoNoBorrow: { borrow: "none" },
  twoBorrow: { borrow: "always" },
  zeros: { minuendDigits: 4, subtrahendDigits: 3, borrow: "zeros" },
  negatives: { minuendDigits: 1, subtrahendDigits: 2, allowNegative: true },
};

const MAX_TOP_CHOICES = ["0", "10", "20", "100", "1000"];
const digitChoices = (max: number) =>
  Array.from({ length: max }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }));

export default function SubtractionWorksheetGeneratorWidget() {
  const strings = useToolUi(SUBTRACTION_WORKSHEET_GENERATOR_STRINGS);
  const [settings, update] = useCommonSettings(defaultSettings({ count: 20 }));
  const seed = useWorksheetSeed();
  const [fields, setFields] = useState<Fields>(START);
  const [missing, setMissing] = useState<MissingMode>("answer");
  const [boxes, setBoxes] = useState(false);

  const patch = (next: Partial<Fields>) =>
    setFields((current) => ({ ...current, ...next }));
  const oneLine = settings.layout === "horizontal";
  const generated = generateSubtraction(
    { ...fields, count: settings.count, missing: oneLine ? missing : "answer" },
    createRng(seed.seed)
  );
  const zerosTooShort = fields.borrow === "zeros" && fields.minuendDigits < 3;

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="space-y-2">
          <p className={LABEL}>{strings.presetsLabel}</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((key) => (
              <button
                className={BUTTON_SMALL}
                key={key}
                onClick={() => patch({ ...START, ...PRESETS[key] })}
                type="button"
              >
                {strings.presets[key]}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id="sw-minuend"
            label={strings.minuendLabel}
            onChange={(value) => patch({ minuendDigits: Number(value) })}
            options={digitChoices(5)}
            value={String(fields.minuendDigits)}
          />
          <SelectField
            hint={strings.subtrahendHint}
            id="sw-subtrahend"
            label={strings.subtrahendLabel}
            onChange={(value) => patch({ subtrahendDigits: Number(value) })}
            options={digitChoices(5)}
            value={String(fields.subtrahendDigits)}
          />
          <SelectField
            id="sw-borrow"
            label={strings.borrowLabel}
            onChange={(borrow: BorrowMode) => patch({ borrow })}
            options={(["any", "none", "always", "zeros"] as const).map(
              (value) => ({ value, label: strings.borrows[value] })
            )}
            value={fields.borrow}
          />
          <SelectField
            id="sw-max-top"
            label={strings.maxTopLabel}
            onChange={(value) => patch({ maxMinuend: Number(value) })}
            options={MAX_TOP_CHOICES.map((value) => ({
              value,
              label: value === "0" ? strings.maxTopNone : value,
            }))}
            value={String(fields.maxMinuend)}
          />
        </div>
        <SelectField
          hint={oneLine ? undefined : strings.unknownHint}
          id="sw-unknown"
          label={strings.unknownLabel}
          onChange={setMissing}
          options={(["answer", "missing", "mixed"] as const).map((value) => ({
            value,
            label: strings.unknowns[value],
          }))}
          value={oneLine ? missing : "answer"}
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={fields.allowNegative}
            label={strings.allowNegative}
            onChange={(allowNegative) => patch({ allowNegative })}
          />
          <Checkbox
            checked={fields.allowZero}
            label={strings.allowZero}
            onChange={(allowZero) => patch({ allowZero })}
          />
          <Checkbox
            checked={boxes}
            label={strings.regroupBoxes}
            onChange={setBoxes}
          />
        </div>
        {zerosTooShort ? <Notice>{strings.zerosNotice}</Notice> : null}
        <CommonControls
          idPrefix="sw"
          onChange={update}
          seed={seed}
          settings={settings}
          strings={strings}
        />
        <UnmetNotice strings={strings} unmet={generated.unmet} />
      </div>
      <ArithmeticSheet
        problems={generated.problems}
        scaffoldBoxes={boxes && !oneLine ? fields.minuendDigits : 0}
        seed={seed.seed}
        settings={settings}
        strings={strings}
        title={settings.title.trim() || strings.defaultTitle}
        workRows={0}
      />
    </div>
  );
}
